import { Fragment, useEffect, useState, type CSSProperties } from 'react';
import { customText, type CustomField, type CustomTarget, type CustomValues } from '../components/CustomFields.tsx';
import { t } from '../i18n/index.ts';
import { addressLine, ownIdLines } from './taxes.ts';
import { PAPER_ACCENTS, PAPER_DEFAULT_ACCENT } from '../theme/paper.ts';
import { action } from './api.ts';
import { useSession } from './session.tsx';

/**
 * Document templates (Settings → Document templates): how the A4 papers look. The papers
 * (invoice, quote, statement) take a `look`; without one they print as they always did. A look
 * only changes colours, letters, the logo and which optional parts print: the seller and buyer,
 * the lines, VAT by rate, the totals and the tax office's codes always print.
 */

export type DocKind = 'invoice' | 'quote' | 'statement';
export const DOC_KINDS: DocKind[] = ['invoice', 'quote', 'statement'];
export interface Look {
  style: 'classic' | 'band' | 'minimal';
  accent: string;
  font: 'sans' | 'serif';
  logo_size: 's' | 'm' | 'l';
  logo_place: 'left' | 'right';
  lines: { number: boolean; unit: boolean };
  show: { bank: boolean; terms: boolean; issued_by: boolean; fields: boolean; signatures: boolean };
  header_note: string | null;
  footer: string | null;
}
export interface DocTemplate {
  id: string;
  name: string;
  look: Look;
  logo: string | null;
  documents: DocKind[];
}
/** What a paper needs: the look and the logo. */
export interface PaperLook {
  look: Look;
  logo: string | null;
}

/** How the papers looked before templates; also what a new template starts from. */
export const DEFAULT_LOOK: Look = {
  style: 'classic',
  accent: PAPER_DEFAULT_ACCENT,
  font: 'sans',
  logo_size: 'm',
  logo_place: 'left',
  lines: { number: true, unit: true },
  show: { bank: true, terms: true, issued_by: true, fields: false, signatures: false },
  header_note: null,
  footer: null,
};
export const ACCENTS = PAPER_ACCENTS;

/**
 * The look to print one kind of document with: the default template for it, or the one the person
 * picks. Null while loading, when there is no template, or when templates are not in the plan.
 */
export function useDocLook(document: DocKind) {
  const { can } = useSession();
  const on = can('studio.use');
  const [picked, setPicked] = useState<string | null>(null);
  const [state, setState] = useState<{ templates: { id: string; name: string; default: boolean }[]; template: DocTemplate | null }>({ templates: [], template: null });
  useEffect(() => {
    if (!on) return;
    let live = true;
    action<typeof state>('studio.doc_look', { document, id: picked }).then(
      (r) => live && setState(r),
      // The paper still prints, in its plain look.
      () => live && setState({ templates: [], template: null }),
    );
    return () => {
      live = false;
    };
  }, [on, document, picked]);
  const paper: PaperLook | null = state.template ? { look: state.template.look, logo: state.template.logo } : null;
  return { paper, templates: state.templates, current: state.template?.id ?? null, pick: setPicked };
}

/** The class and colour variables of the sheet. */
export function paperAttrs(p: PaperLook | null | undefined, base = 'inv-paper'): { className: string; style?: CSSProperties } {
  if (!p) return { className: base };
  const l = p.look;
  return { className: `${base} look look-${l.style} look-${l.font}`, style: { '--look-accent': l.accent } as CSSProperties };
}

interface Seller {
  legal_name: string;
  trade_name: string | null;
  tax_id: string | null;
  vat_id?: string | null;
  reg_no?: string | null;
  address: string | null;
  city: string | null;
  postcode?: string | null;
  region?: string | null;
  phone: string | null;
  email: string | null;
}

export const logoSrc = (logo: string) => `data:image/${logo.startsWith('iVBOR') ? 'png' : 'jpeg'};base64,${logo}`;

/** The seller's block at the top left, with the logo when it goes on the left. */
export function PaperSeller({ c, p }: { c: Seller; p?: PaperLook | null }) {
  return (
    <div className="inv-paper-seller">
      {p?.logo && p.look.logo_place === 'left' ? <PaperLogo p={p} /> : null}
      <p className="inv-seller">{c.trade_name || c.legal_name}</p>
      {c.trade_name && c.trade_name !== c.legal_name ? <p>{c.legal_name}</p> : null}
      {ownIdLines(c).map((line) => (
        <p key={line}>{line}</p>
      ))}
      <p>{addressLine(c)}</p>
      <p>{[c.phone, c.email].filter(Boolean).join(' · ')}</p>
      {p?.look.header_note ? <p className="look-note">{p.look.header_note}</p> : null}
    </div>
  );
}

/** The logo above the title, when it goes on the right. */
export const PaperLogoRight = ({ p }: { p?: PaperLook | null }) => (p?.logo && p.look.logo_place === 'right' ? <PaperLogo p={p} /> : null);

function PaperLogo({ p }: { p: PaperLook }) {
  return <img className={`look-logo look-logo-${p.look.logo_size}`} src={logoSrc(p.logo!)} alt="" />;
}

/** Signature boxes and the footer, at the bottom of the paper. */
export function PaperEnd({ p, by, buyerLabel }: { p?: PaperLook | null; by?: string | null; buyerLabel?: string }) {
  if (!p) return null;
  const { show, footer } = p.look;
  if (!show.signatures && !footer) return null;
  return (
    <>
      {show.signatures ? (
        <section className="look-signs" aria-label={t('look.p_signatures')}>
          <div>
            <span className="look-sign-line" />
            <p>{t('look.p_seller_sign')}</p>
            {by ? <p className="muted">{by}</p> : null}
          </div>
          <div>
            <span className="look-sign-line" />
            <p>{buyerLabel ?? t('look.p_buyer_sign')}</p>
          </div>
        </section>
      ) : null}
      {footer ? <p className="look-footer">{footer}</p> : null}
    </>
  );
}

/** Lets the person print with another template, when there is more than one. */
export function LookPicker({ templates, current, pick }: { templates: { id: string; name: string }[]; current: string | null; pick: (id: string) => void }) {
  if (templates.length < 2) return null;
  return (
    <label className="look-picker">
      <span className="muted small">{t('look.print_with')}</span>
      <select className="input" value={current ?? ''} onChange={(e) => pick(e.target.value)} aria-label={t('look.print_with')}>
        {templates.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * The fields a business added to the document and its customer, when the template prints them.
 * Only filled-in fields print, each with its name.
 */
export function PaperFields({ p, of, sample }: { p?: PaperLook | null; of: [CustomTarget, string | null | undefined][]; sample?: { label: string; text: string }[] }) {
  const on = !!p?.look.show.fields && !sample;
  const key = JSON.stringify(of);
  const [rows, setRows] = useState<{ label: string; text: string }[]>([]);
  useEffect(() => {
    if (!on) return setRows([]);
    let live = true;
    const wanted = (JSON.parse(key) as [CustomTarget, string | null][]).filter(([, r]) => r);
    Promise.all(
      wanted.map(async ([target, record]) => {
        const [{ fields }, values] = await Promise.all([
          action<{ fields: CustomField[] }>('studio.fields', { target }),
          action<Record<string, CustomValues>>('studio.values', { target, records: [record] }),
        ]);
        const v = values[record!] ?? {};
        return fields.filter((f) => v[f.key] !== undefined && v[f.key] !== '').map((f) => ({ label: f.label, text: customText(f, v[f.key]) }));
      }),
    ).then(
      (r) => live && setRows(r.flat()),
      () => live && setRows([]),
    );
    return () => {
      live = false;
    };
  }, [on, key]);
  const shown = p?.look.show.fields ? (sample ?? rows) : [];
  if (!shown.length) return null;
  return (
    <dl className="look-fields">
      {shown.map((r, i) => (
        <Fragment key={i}>
          <dt>{r.label}</dt>
          <dd>{r.text}</dd>
        </Fragment>
      ))}
    </dl>
  );
}
